import { useState } from 'react';

import {
  Box,
  Button,
  Card,
  Divider,
  DropdownSelect,
  IconButton,
  Menu,
  Switch,
  Text,
  Tooltip,
} from '@zillow/constellation-10';
import { IconCloseFilled, IconInfoFilled } from '@zillow/constellation-icons';

import { useQueryClient } from '@zg-rentals/react-query5';
import { PlacementSource } from '@zg-rentals/ts-rental-manager-api-client';

import { useAutoSpotlight } from './AutoSpotlightContext';
import { OPTIMIZER_LIST } from './rabDeployment';
import { SmartAssignActivationModal } from './SmartAssignActivationModal';
import { SPOTLIGHT_COPY as COPY } from './spotlightCopy';
import { getCurrentPlacementSource } from './utils/cacheHelpers';

import type { OptimizerId } from './rabDeployment';

const INFO_ICON = (
  <IconButton
    title="Info"
    size="sm"
    emphasis="bare"
    shape="circle"
    css={{ color: 'icon.muted', width: 'icon.sm', height: 'icon.sm' }}
    icon={<IconInfoFilled />}
  />
);

export function SmartAssignHeader() {
  const ctx = useAutoSpotlight();
  const queryClient = useQueryClient();
  const [activationOpen, setActivationOpen] = useState(false);

  if (!ctx) {
    return null;
  }

  const {
    autoCount,
    availableCount,
    batchExclude,
    batchPin,
    batchUnexclude,
    batchUnpin,
    clearSelection,
    excludedIds,
    isDisablingSmartAssign,
    isOverviewLoaded,
    isSelectionMode,
    licenses,
    onChangeOptimizer,
    optimizer,
    pinnedCount,
    pinnedIds,
    requestDisableSmartAssign,
    selectedIds,
    smartConfigMode,
    toggleSelectionMode,
  } = ctx;

  const smartAssignEnabled = smartConfigMode !== null;
  const inSpotlight = pinnedCount + autoCount;

  const handleToggle = () => {
    if (!smartAssignEnabled) {
      setActivationOpen(true);
      return;
    }
    requestDisableSmartAssign();
  };

  const options = OPTIMIZER_LIST.map((o) => ({ label: o.label, value: o.id, meta: o.description }));
  const selectedCount = selectedIds.size;
  const selectedAliases = Array.from(selectedIds);

  const isPinned = (id: string) => {
    const cached = getCurrentPlacementSource(queryClient, id);
    return cached != null ? cached === PlacementSource.PINNED : pinnedIds.has(id);
  };
  const isExcluded = (id: string) => {
    const cached = getCurrentPlacementSource(queryClient, id);
    return cached != null ? cached === PlacementSource.EXCLUDED : excludedIds.has(id);
  };

  const anyPinned = selectedAliases.some(isPinned);
  const allPinned = selectedAliases.length > 0 && selectedAliases.every(isPinned);
  const anyExcluded = selectedAliases.some(isExcluded);
  const allExcluded = selectedAliases.length > 0 && selectedAliases.every(isExcluded);
  const overLimit = isOverviewLoaded && selectedCount > availableCount;

  const bulkActions: Array<{ label: string; onSelect: () => void; disabled?: boolean }> = [
    ...(!anyPinned ? [{ label: 'Pin', onSelect: () => batchPin(selectedAliases), disabled: overLimit }] : []),
    ...(allPinned ? [{ label: 'Unpin', onSelect: () => batchUnpin(selectedAliases) }] : []),
    ...(!anyExcluded ? [{ label: 'Exclude', onSelect: () => batchExclude(selectedAliases) }] : []),
    ...(allExcluded ? [{ label: 'Unexclude', onSelect: () => batchUnexclude(selectedAliases) }] : []),
  ];

  return (
    <Card data-testid="smart-assign-header" css={{ marginBlock: '400', padding: '500' }}>
      <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '200' }}>
        <Box css={{ display: 'flex', alignItems: 'center', gap: '300' }}>
          <Text textStyle="heading-sm">{COPY.product.spotlight}</Text>
          <Box
            css={{
              display: 'flex',
              alignItems: 'baseline',
              paddingLeft: '300',
              borderLeft: '1px solid {colors.border.subtle}',
            }}
          >
            <Text
              textStyle={availableCount > 0 ? 'body-sm-bold' : 'body-sm'}
              asChild
              css={{ color: availableCount > 0 ? '{colors.text.accent.blue.strong}' : '{colors.text.subtle}' }}
            >
              <span>
                {availableCount} Available {availableCount === 1 ? 'placement' : 'placements'}
              </span>
            </Text>
            <Text textStyle="body-sm" asChild css={{ color: '{colors.text.subtle}' }}>
              <span>
                {smartAssignEnabled
                  ? ` · ${pinnedCount} Pinned · ${autoCount} Smart Assign`
                  : ` · ${inSpotlight} in Spotlight`}
              </span>
            </Text>
          </Box>
        </Box>
        <Box css={{ display: 'flex', alignItems: 'center', gap: '200' }}>
          <Text textStyle="body-sm-bold">{COPY.smartAssign.toggleLabel}</Text>
          <Switch
            controlId="smart-assign-toggle"
            aria-label={COPY.smartAssign.toggleLabel}
            checked={smartAssignEnabled}
            disabled={isDisablingSmartAssign}
            onChange={handleToggle}
          />
          <Tooltip content={COPY.smartAssign.tooltip} trigger={INFO_ICON} />
        </Box>
      </Box>

      {smartAssignEnabled && (
        <>
          <Divider css={{ marginBlock: '400' }} />

          <Box css={{ display: 'flex', alignItems: 'center', gap: '300', flexWrap: 'wrap' }}>
            <Box css={{ display: 'flex', alignItems: 'center', gap: '300', flexShrink: 0 }}>
              <Tooltip content={COPY.tooltips.optimizeBy} trigger={INFO_ICON} />
              <Text textStyle="body-sm-bold" css={{ color: '{colors.text.neutral}' }}>
                Optimize by
              </Text>
              <DropdownSelect
                value={optimizer}
                options={options}
                onChange={(value) => onChangeOptimizer((Array.isArray(value) ? value[0] : value) as OptimizerId)}
                showLabelForValue
                size="sm"
                css={{ width: '220px' }}
              />
            </Box>

            {isSelectionMode ? (
              <Box css={{ display: 'flex', alignItems: 'center', gap: '200', marginInlineStart: 'auto' }}>
                <Text textStyle="body-sm">{selectedCount} selected</Text>
                <IconButton
                  title="Clear selection"
                  size="sm"
                  emphasis="bare"
                  shape="circle"
                  icon={<IconCloseFilled />}
                  onClick={clearSelection}
                />
                {selectedCount > 0 && bulkActions.length > 0 && (
                  <Menu
                    trigger={
                      <Button size="sm" emphasis="outlined">
                        Action ▾
                      </Button>
                    }
                    content={bulkActions.map(({ label, onSelect, disabled }) => (
                      <Menu.Item key={label} onClick={disabled ? undefined : onSelect} disabled={disabled}>
                        <Menu.ItemLabel>{label}</Menu.ItemLabel>
                      </Menu.Item>
                    ))}
                  />
                )}
              </Box>
            ) : (
              <Button size="sm" emphasis="outlined" onClick={toggleSelectionMode} css={{ marginInlineStart: 'auto' }}>
                Multi-select
              </Button>
            )}
          </Box>
        </>
      )}

      {activationOpen && (
        <SmartAssignActivationModal
          open
          onClose={() => setActivationOpen(false)}
          inUse={inSpotlight}
          total={licenses}
        />
      )}
    </Card>
  );
}

export default SmartAssignHeader;
